import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Loader2, Sparkles, Copy, Twitter, Linkedin, Instagram,
  Facebook, MessageCircle, Mail, FileText, Globe, ExternalLink, AtSign,
} from "lucide-react";

const PLATFORM_META = {
  twitter_thread: { label: "Twitter thread", icon: Twitter, color: "text-sky-400" },
  linkedin_post: { label: "LinkedIn post", icon: Linkedin, color: "text-blue-500" },
  instagram_caption: { label: "Instagram caption", icon: Instagram, color: "text-pink-400" },
  whatsapp_broadcast: { label: "WhatsApp broadcast", icon: MessageCircle, color: "text-emerald-400" },
  facebook_post: { label: "Facebook post", icon: Facebook, color: "text-blue-400" },
  threads_post: { label: "Threads", icon: AtSign, color: "text-white" },
  email_subject_body: { label: "Email", icon: Mail, color: "text-violet-400" },
  blog_teaser: { label: "Blog teaser", icon: FileText, color: "text-amber-400" },
};

export default function OneClickCampaign() {
  const [url, setUrl] = useState("");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const generate = async (e) => {
    e?.preventDefault();
    if (!url.startsWith("http")) { toast.error("Enter a full URL"); return; }
    if (brief.trim().length < 10) { toast.error("Describe the campaign in one sentence"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/campaign/one-click/generate", { url, brief });
      setData(data);
      toast.success("8 captions generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="space-y-8" data-testid="one-click-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Megaphone className="h-3.5 w-3.5" /> Campaign in one click
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">One-click campaign</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Paste any URL and a one-line brief. The AI returns 8 platform-tailored share captions — in your brand voice — ready to copy, preview, or schedule.
        </p>
      </header>

      <form onSubmit={generate} className="glass rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3">
          <Globe className="h-4 w-4 text-white/40" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://makeyourbrand.in/blog/launch-announcement"
            data-testid="oneclick-url"
            className="flex-1 py-3 text-sm outline-none bg-transparent"
          />
        </div>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="One-line brief: e.g. 'Launching our Power Hour feature — 60-min batch of 30 posts + 5 blogs + 10 images for any brand.'"
          rows={3}
          data-testid="oneclick-brief"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <button type="submit" disabled={busy} data-testid="oneclick-generate"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate 8 captions
        </button>
      </form>

      {data && (
        <>
          {data.landing_meta?.title && (
            <div className="glass rounded-xl p-4 text-sm" data-testid="oneclick-landing">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">Landing page</div>
              <div className="font-semibold">{data.landing_meta.title}</div>
              <a href={data.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-300 inline-flex items-center gap-1 mt-1">{data.url} <ExternalLink className="h-3 w-3" /></a>
            </div>
          )}

          <section className="grid lg:grid-cols-2 gap-4" data-testid="oneclick-captions">
            {Object.keys(PLATFORM_META).map((key) => {
              const meta = PLATFORM_META[key];
              const value = data.captions?.[key];
              if (!value) return null;
              const Icon = meta.icon;
              const isEmail = key === "email_subject_body" && typeof value === "object";
              const text = isEmail
                ? `Subject: ${value.subject}\n\n${value.body}`
                : (typeof value === "string" ? value : JSON.stringify(value));

              return (
                <div key={key} className="glass rounded-2xl p-5" data-testid={`caption-${key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${meta.color}`} />
                      <span className="font-heading font-black text-sm">{meta.label}</span>
                    </div>
                    <button onClick={() => copy(text)} data-testid={`copy-${key}`}
                      className="inline-flex items-center gap-1 text-xs glass rounded px-2 py-1 hover:bg-white/5"><Copy className="h-3 w-3" /> Copy</button>
                  </div>
                  {isEmail ? (
                    <div className="space-y-2">
                      <div className="bg-black/40 border border-white/10 rounded p-2 text-xs">
                        <div className="text-white/40 mb-1">Subject</div>
                        <div className="text-white font-semibold">{value.subject}</div>
                      </div>
                      <div className="bg-black/40 border border-white/10 rounded p-3 text-xs text-white/80 whitespace-pre-wrap">{value.body}</div>
                    </div>
                  ) : (
                    <pre className="bg-black/40 border border-white/10 rounded p-3 text-xs text-white/85 whitespace-pre-wrap font-sans">{text}</pre>
                  )}
                  <div className="text-[10px] text-white/30 font-mono mt-2 text-right">{text.length} chars</div>
                </div>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}
